import { useEffect, useMemo, useReducer, useState } from "react"
import { ActionIcon, Button, Flex, Group, Modal, Stack, Text, TextInput, Tooltip } from "@mantine/core"
import { DataTable, DataTableSortStatus } from "mantine-datatable"
import { useClinics, useCreateClinic, useDeleteClinic, useUpdateClinic } from "@features/clinic/hooks/use-clinics.ts"
import { IconEdit, IconPlus, IconSearch, IconTrash, IconX } from "@tabler/icons-react"
import { PAGE_SIZES, ROLES } from "@/shared"
import _ from "lodash"
import { useAuth } from "@features/auth/hooks/useAuth.ts"
import { useDynamicHeight } from "@/shared/hooks/responsive.queries"
import { headerStore } from "@shared/state/header.state.ts"
import { openConfirmModal } from "@mantine/modals"
import { Clinic } from "../domain/clinic.types"

interface SearchState {
  nombre: string
  direccion: string
  ciudad: string
  telefono: string
}

interface State {
  search: SearchState
  sortStatus: DataTableSortStatus<Clinic>
  page: number
  pageSize: number
}

type Action = { type: "SET_SEARCH"; payload: Partial<SearchState> } | { type: "SET_SORT_STATUS"; payload: DataTableSortStatus<Clinic> } | { type: "SET_PAGE"; payload: number } | { type: "SET_PAGE_SIZE"; payload: number }

const initialState: State = {
  search: {
    nombre: "",
    direccion: "",
    ciudad: "",
    telefono: ""
  },
  sortStatus: { columnAccessor: "nombre", direction: "asc" },
  page: 1,
  pageSize: PAGE_SIZES[1]
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_SEARCH":
      return { ...state, search: { ...state.search, ...action.payload }, page: 1 }
    case "SET_SORT_STATUS":
      return { ...state, sortStatus: action.payload }
    case "SET_PAGE":
      return { ...state, page: action.payload }
    case "SET_PAGE_SIZE":
      return { ...state, pageSize: action.payload, page: 1 }
    default:
      return state
  }
}

export default function ClinicsPage() {
  const { hasRole } = useAuth()
  const { tableHeight } = useDynamicHeight()
  const { data: clinics, isLoading: fetching, refetch } = useClinics()
  const createClinicMutation = useCreateClinic()
  const updateClinicMutation = useUpdateClinic()
  const deleteClinicMutation = useDeleteClinic()

  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    headerStore.setTitle("Gestión de Clínicas")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  const [opened, setOpened] = useState(false)
  const [editingClinic, setEditingClinic] = useState<Clinic | null>(null)
  const [newClinic, setNewClinic] = useState<Partial<Clinic>>({ nombre: "", direccion: "", ciudad: "", telefono: "" })

  const handleCreate = async () => {
    if (editingClinic) {
      await updateClinicMutation.mutateAsync({ id: editingClinic.id, clinicData: newClinic })
    } else {
      await createClinicMutation.mutateAsync(newClinic)
    }
    setOpened(false)
    setEditingClinic(null)
    setNewClinic({ nombre: "", direccion: "", ciudad: "", telefono: "" })
  }

  const handleEdit = (clinic: Clinic) => {
    setEditingClinic(clinic)
    setNewClinic({
      nombre: clinic.nombre || "",
      direccion: clinic.direccion || "",
      ciudad: clinic.ciudad || "",
      telefono: clinic.telefono || ""
    })
    setOpened(true)
  }

  const handleDelete = async (id: string) => {
    openConfirmModal({
      title: "Eliminar clínica",
      centered: true,
      children: <Text size="sm">¿Está seguro de eliminar esta clínica? Esta acción no se puede deshacer.</Text>,
      labels: { confirm: "Eliminar", cancel: "Cancelar" },
      confirmProps: { color: "red", loading: deleteClinicMutation.isPending, leftSection: <IconTrash size={15} /> },
      onConfirm: async () => {
        await deleteClinicMutation.mutateAsync(id)
      }
    })
  }

  const filteredRecords = useMemo(() => {
    if (!clinics) return []
    let result = [...clinics]

    const { nombre, direccion, ciudad, telefono } = state.search

    if (nombre) result = result.filter((r) => r.nombre?.toLowerCase().includes(nombre.toLowerCase()))
    if (direccion) result = result.filter((r) => r.direccion?.toLowerCase().includes(direccion.toLowerCase()))
    if (ciudad) result = result.filter((r) => r.ciudad?.toLowerCase().includes(ciudad.toLowerCase()))
    if (telefono) result = result.filter((r) => r.telefono?.toLowerCase().includes(telefono.toLowerCase()))

    result = _.orderBy(result, [state.sortStatus.columnAccessor], [state.sortStatus.direction])

    return result
  }, [clinics, state.search, state.sortStatus])

  const pagedRecords = useMemo(() => {
    const from = (state.page - 1) * state.pageSize
    const to = from + state.pageSize
    return filteredRecords.slice(from, to)
  }, [filteredRecords, state.page, state.pageSize])

  const clearSearch = (field: keyof SearchState) => {
    dispatch({ type: "SET_SEARCH", payload: { [field]: "" } })
  }

  return (
    <Stack gap="md">
      <Flex justify="flex-end" align="center">
        {(hasRole(ROLES.ADMIN) || hasRole(ROLES.CLINIC_CREATE)) && (
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => {
              setEditingClinic(null)
              setNewClinic({ nombre: "", direccion: "", ciudad: "", telefono: "" })
              setOpened(true)
            }}
          >
            Agregar Clínica
          </Button>
        )}
      </Flex>

      <DataTable
        withTableBorder
        borderRadius="md"
        striped
        highlightOnHover
        fetching={fetching}
        records={pagedRecords}
        totalRecords={filteredRecords.length}
        recordsPerPage={state.pageSize}
        page={state.page}
        onPageChange={(p) => dispatch({ type: "SET_PAGE", payload: p })}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={(s) => dispatch({ type: "SET_PAGE_SIZE", payload: s })}
        recordsPerPageLabel="Cantidad por página"
        sortStatus={state.sortStatus}
        onSortStatusChange={(s) => dispatch({ type: "SET_SORT_STATUS", payload: s })}
        columns={[
          {
            accessor: "nombre",
            title: "Nombre",
            sortable: true,
            filtering: state.search.nombre !== "",
            filter: (
              <TextInput
                placeholder="Buscar por nombre"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.nombre && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("nombre")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.nombre}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { nombre: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "direccion",
            title: "Dirección",
            sortable: true,
            filtering: state.search.direccion !== "",
            filter: (
              <TextInput
                placeholder="Buscar dirección"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.direccion && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("direccion")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.direccion}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { direccion: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "ciudad",
            title: "Ciudad",
            sortable: true,
            filtering: state.search.ciudad !== "",
            filter: (
              <TextInput
                placeholder="Buscar ciudad"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.ciudad && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("ciudad")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.ciudad}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { ciudad: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "telefono",
            title: "Teléfono",
            sortable: true,
            filtering: state.search.telefono !== "",
            filter: (
              <TextInput
                placeholder="Buscar teléfono"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.telefono && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => clearSearch("telefono")}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.telefono}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { telefono: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "actions",
            title: "Acciones",
            textAlign: "right",
            hidden: !hasRole(ROLES.ADMIN) && !hasRole(ROLES.CLINIC_EDIT) && !hasRole(ROLES.CLINIC_DELETE),
            render: (clinic: Clinic) => (
              <Group gap={4} justify="right" wrap="nowrap">
                {(hasRole(ROLES.ADMIN) || hasRole(ROLES.CLINIC_EDIT)) && (
                  <Tooltip label="Editar Clínica">
                    <ActionIcon size="sm" variant="subtle" color="blue.7" onClick={() => handleEdit(clinic)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {(hasRole(ROLES.ADMIN) || hasRole(ROLES.CLINIC_DELETE)) && (
                  <Tooltip label="Eliminar Clínica">
                    <ActionIcon size="sm" variant="subtle" color="red.7" onClick={() => handleDelete(clinic.id)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            )
          }
        ]}
        style={{ height: tableHeight }}
        noRecordsText="No se encontraron clínicas"
      />

      <Modal opened={opened} onClose={() => setOpened(false)} title={editingClinic ? "Editar Clínica" : "Agregar Nueva Clínica"} centered>
        <Stack>
          <TextInput label="Nombre" placeholder="Nombre de la clínica" value={newClinic.nombre} onChange={(e) => setNewClinic({ ...newClinic, nombre: e.target.value })} required />
          <TextInput label="Dirección" placeholder="Dirección" value={newClinic.direccion} onChange={(e) => setNewClinic({ ...newClinic, direccion: e.target.value })} required />
          <TextInput label="Ciudad" placeholder="Ciudad" value={newClinic.ciudad} onChange={(e) => setNewClinic({ ...newClinic, ciudad: e.target.value })} required />
          <TextInput label="Teléfono" placeholder="Teléfono" value={newClinic.telefono} onChange={(e) => setNewClinic({ ...newClinic, telefono: e.target.value })} />
          <Button onClick={handleCreate} loading={createClinicMutation.isPending || updateClinicMutation.isPending}>
            Guardar
          </Button>
        </Stack>
      </Modal>
    </Stack>
  )
}
